import { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, FileText, History, ShieldCheck, X } from "lucide-react";

const STATUS_CLS = {
  verified: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800",
  rejected: "bg-red-100 text-red-700",
  needs_resubmission: "bg-orange-100 text-orange-800",
  previously_uploaded_not_verified: "bg-secondary text-muted-foreground",
  not_provided: "bg-secondary text-muted-foreground",
};

export default function AdminDbsTab() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);       // cleaner_id being decided
  const [history, setHistory] = useState(null); // { cleaner_id, events }
  const [provider, setProvider] = useState(null);
  const [form, setForm] = useState({ dbs_level: "basic", certificate_issue_date: "", verification_provider: "", provider_reference: "", certificate_type: "", provider_result_date: "", internal_notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/dbs/admin/records").then((r) => setData(r.data)).catch(() => setData({ records: [] }));
  useEffect(() => {
    load();
    api.get("/dbs/provider/status").then((r) => setProvider(r.data)).catch(() => {});
  }, []);

  const startDecision = (r) => {
    setOpen(r.cleaner_id);
    setForm({
      dbs_level: r.dbs_level || "basic",
      certificate_issue_date: r.certificate_issue_date || "",
      verification_provider: r.verification_provider || "manual_admin_review",
      provider_reference: r.provider_reference || "",
      certificate_type: r.certificate_type || "",
      provider_result_date: r.provider_result_date || "",
      internal_notes: r.internal_notes || "",
    });
  };

  const decide = async (cleaner_id, outcome) => {
    setBusy(true);
    try {
      await api.post(`/dbs/admin/${cleaner_id}/decision`, { outcome, ...form });
      toast.success(outcome === "approve" ? "DBS verified" : outcome === "reject" ? "DBS rejected" : "Resubmission requested");
      setOpen(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const showHistory = async (cleaner_id) => {
    const { data } = await api.get(`/dbs/admin/${cleaner_id}/history`);
    setHistory({ cleaner_id, events: data.events });
  };

  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-accent" />;

  return (
    <div className="space-y-5" data-testid="admin-dbs">
      <div>
        <h3 className="font-display font-semibold flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-accent" /> DBS verification</h3>
        <p className="text-sm text-muted-foreground mt-0.5">
          We never order DBS checks — we record the outcome of verifying a certificate the cleaner
          already holds. {data.freshness_note}
        </p>
      </div>

      {provider && (
        <div className={`rounded-2xl border p-4 text-sm ${provider.live ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="dbs-provider-status">
          <p className="font-semibold">
            Automated verification provider: {provider.live ? `LIVE (${provider.selected})` : provider.state === "manual" ? "manual review (no provider connected)" : provider.state.replace(/_/g, " ")}
          </p>
          <p className="text-muted-foreground mt-1">{provider.message}</p>
          {!provider.live && (
            <>
              <p className="mt-2 text-xs text-muted-foreground">
                Researched candidates (none contracted): {provider.candidates.map((c) => c.name).join(" · ")}
              </p>
              <ul className="mt-2 text-xs text-muted-foreground list-disc pl-5 space-y-0.5">
                {provider.requirements_before_go_live.map((r) => <li key={r}>{r}</li>)}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="dbs-counts">
        {Object.entries(data.counts || {}).map(([k, v]) => (
          <span key={k} className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_CLS[k] || "bg-secondary"}`}>{k.replace(/_/g, " ")}: {v}</span>
        ))}
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-secondary">no verified DBS: {data.cleaners_without_verified_dbs}</span>
      </div>

      {data.records.length === 0 ? (
        <p className="text-muted-foreground py-10 text-center">No DBS records yet.</p>
      ) : data.records.map((r) => (
        <div key={r.cleaner_id} className="bg-card rounded-2xl border border-primary/5 p-5" data-testid={`dbs-record-${r.cleaner_id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium">{r.cleaner_name || r.cleaner_id}</p>
            <span className="px-2 py-0.5 rounded-full bg-secondary text-xs font-semibold" data-testid={`dbs-mccref-${r.cleaner_id}`}>{r.mcc_ref || "no ref"}</span>
            <span className="text-sm text-muted-foreground">{r.cleaner_email}</span>
            <span className={`ml-auto px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_CLS[r.verification_status] || "bg-secondary"}`} data-testid={`dbs-status-${r.cleaner_id}`}>
              {r.simple_status?.label || r.verification_status.replace(/_/g, " ")}
            </span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 mt-3 text-sm">
            <Item k="Declaration" v={(r.declaration || "—").replace(/_/g, " ")} />
            <Item k="DBS type" v={r.dbs_level || "—"} />
            <Item k="Certificate issued" v={r.certificate_issue_date || "—"} />            <Item k="Verified on" v={r.verification_date || "—"} />
            <Item k="Re-verification due" v={r.reverification_due_date || "—"} />
            <Item k="Provider" v={r.verification_provider || "—"} />
            <Item k="Provider reference" v={r.provider_reference || "—"} />
            <Item k="Badge status" v={r.badge_status} />
            <Item k="Public status" v={r.public_status} />
            <Item k="Update Service" v={r.update_service_checked ? "yes" : "no"} />
            <Item k="Certificate on file" v={r.document_uploaded ? "yes" : "no"} />
          </div>
          {r.internal_notes && <p className="text-sm mt-2 italic">Notes: {r.internal_notes}</p>}

          <div className="flex flex-wrap gap-2 mt-4">
            <button onClick={() => startDecision(r)} className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium" data-testid={`dbs-review-${r.cleaner_id}`}>Review</button>
            {r.document_uploaded && (
              <a href={`${API_BASE}/dbs/admin/${r.cleaner_id}/document`} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-full border border-border text-sm font-medium inline-flex items-center gap-1.5 hover:bg-secondary transition-colors" data-testid={`dbs-doc-${r.cleaner_id}`}>
                <FileText className="h-4 w-4" /> View certificate
              </a>
            )}
            <button onClick={() => showHistory(r.cleaner_id)} className="px-4 py-2 rounded-full border border-border text-sm font-medium inline-flex items-center gap-1.5 hover:bg-secondary transition-colors" data-testid={`dbs-history-${r.cleaner_id}`}>
              <History className="h-4 w-4" /> History
            </button>
          </div>

          {open === r.cleaner_id && (
            <div className="mt-4 border-t border-border pt-4 space-y-3" data-testid="dbs-decision-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-sm">DBS type
                  <select value={form.dbs_level} onChange={(e) => setForm({ ...form, dbs_level: e.target.value })} className="adbs-input" data-testid="dbs-decision-level">
                    {["basic", "standard", "enhanced", "unknown"].map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </label>
                <label className="text-sm">Certificate issue date
                  <input type="date" value={form.certificate_issue_date} onChange={(e) => setForm({ ...form, certificate_issue_date: e.target.value })} className="adbs-input" data-testid="dbs-decision-issue-date" />
                </label>
                <label className="text-sm">Verification provider
                  <input value={form.verification_provider} onChange={(e) => setForm({ ...form, verification_provider: e.target.value })} placeholder="manual_admin_review" className="adbs-input" data-testid="dbs-decision-provider" />
                </label>
                <label className="text-sm">Provider reference
                  <input value={form.provider_reference} onChange={(e) => setForm({ ...form, provider_reference: e.target.value })} className="adbs-input" data-testid="dbs-decision-reference" />
                </label>
                <label className="text-sm">Certificate / check type
                  <input value={form.certificate_type} onChange={(e) => setForm({ ...form, certificate_type: e.target.value })} placeholder="e.g. Basic DBS" className="adbs-input" data-testid="dbs-decision-cert-type" />
                </label>
                <label className="text-sm">Provider result date
                  <input type="date" value={form.provider_result_date} onChange={(e) => setForm({ ...form, provider_result_date: e.target.value })} className="adbs-input" data-testid="dbs-decision-result-date" />
                </label>
              </div>
              <label className="text-sm block">Internal notes
                <textarea rows={2} value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} className="adbs-input" data-testid="dbs-decision-notes" />
              </label>
              <div className="flex flex-wrap gap-2">
                <button disabled={busy} onClick={() => decide(r.cleaner_id, "approve")} className="px-4 py-2 rounded-full bg-green-600 text-white text-sm font-medium disabled:opacity-50" data-testid={`dbs-approve-${r.cleaner_id}`}>Approve</button>
                <button disabled={busy} onClick={() => decide(r.cleaner_id, "request_resubmission")} className="px-4 py-2 rounded-full bg-amber-500 text-white text-sm font-medium disabled:opacity-50" data-testid={`dbs-resubmit-${r.cleaner_id}`}>Request resubmission</button>
                <button disabled={busy} onClick={() => decide(r.cleaner_id, "reject")} className="px-4 py-2 rounded-full border border-border text-sm font-medium disabled:opacity-50" data-testid={`dbs-reject-${r.cleaner_id}`}>Reject</button>
                <button onClick={() => setOpen(null)} className="px-3 py-2 rounded-full border border-border text-sm"><X className="h-4 w-4" /></button>
              </div>
            </div>
          )}

          {history?.cleaner_id === r.cleaner_id && (
            <div className="mt-4 bg-secondary/40 rounded-xl p-3 space-y-1 max-h-48 overflow-y-auto" data-testid="dbs-history-list">
              {history.events.length === 0 ? <p className="text-xs text-muted-foreground">No history yet.</p> :
                history.events.map((e) => (
                  <p key={e.event_id} className="text-xs">
                    <span className="font-medium">{e.action.replace(/_/g, " ")}</span> · {new Date(e.created_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
                  </p>
                ))}
            </div>
          )}
        </div>
      ))}
      <style>{`.adbs-input{width:100%;margin-top:0.25rem;padding:0.5rem 0.7rem;border-radius:0.75rem;border:1px solid hsl(var(--border));background:hsl(var(--background));outline:none}`}</style>
    </div>
  );
}

function Item({ k, v }) {
  return <p><span className="text-muted-foreground">{k}: </span><span className="font-medium">{v}</span></p>;
}
